/**
 * updatePropertySchema — SCRUM-51 step 4 (interface-smith GREEN).
 *
 * Zod schema for PATCH /properties/:id request body. All fields are optional
 * but the payload must contain at least one recognized field (empty payload
 * is rejected to avoid meaningless version bumps).
 */
import { z } from 'zod';
// PR 116 cycle-5 H1: hard-invariant rejection at the HTTP boundary.
// (inactive, bookable) is forbidden in the domain (UnitStatus.create throws
// a plain Error). Catching it here means a malformed wire payload returns
// 400 with a structured Zod issue, instead of 500 from an uncaught domain
// Error. The domain VO + DB CHECK stay authoritative.
const unitStatusSchema = z
    .object({
    billing: z.enum(['active', 'inactive']),
    visibility: z.enum(['bookable', 'non_bookable']),
})
    .refine((s) => !(s.billing === 'inactive' && s.visibility === 'bookable'), {
    message: 'forbidden status combination: an inactive property cannot be bookable',
    path: ['visibility'],
});
export const updatePropertySchema = z
    .object({
    unitName: z.string().min(1).max(255).optional(),
    unitCategory: z
        .enum([
        'home',
        'condominium',
        'villa',
        'cabin',
        'townhome',
        'apartment',
        'other',
    ])
        .optional(),
    listingType: z
        .enum(['entire_unit', 'private_room', 'shared_room', 'other'])
        .nullable()
        .optional(),
    // PR 116 cycle-4 M1: count Unicode codepoints (matches domain VO's
    // char_length), not UTF-16 code units. Lite product cap = 500 codepoints
    // per DS-007 v8 §3.2 / v7 S1.
    description: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 500, {
        message: 'description must be at most 500 Unicode codepoints',
    }),
    unitAlias: z.string().max(255).nullable().optional(),
    status: unitStatusSchema.optional(),
    licenseNumber: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 100, {
        message: 'licenseNumber must be at most 100 Unicode codepoints',
    }),
    vatNumber: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 64, {
        message: 'vatNumber must be at most 64 Unicode codepoints',
    }),
    houseRules: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 4000, {
        message: 'houseRules must be at most 4000 Unicode codepoints',
    }),
    aboutThisSpace: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 4000, {
        message: 'aboutThisSpace must be at most 4000 Unicode codepoints',
    }),
    additionalRules: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 4000, {
        message: 'additionalRules must be at most 4000 Unicode codepoints',
    }),
    localLaws: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 4000, {
        message: 'localLaws must be at most 4000 Unicode codepoints',
    }),
})
    .strip()
    .refine((data) => data.unitName !== undefined ||
    data.unitCategory !== undefined ||
    data.listingType !== undefined ||
    data.description !== undefined ||
    data.unitAlias !== undefined ||
    data.status !== undefined ||
    data.licenseNumber !== undefined ||
    data.vatNumber !== undefined ||
    data.houseRules !== undefined ||
    data.aboutThisSpace !== undefined ||
    data.additionalRules !== undefined ||
    data.localLaws !== undefined, {
    message: 'At least one field must be provided in the update payload',
});
//# sourceMappingURL=update-property.schema.js.map